import React from "react";
// Material imports
import { useTheme } from "@mui/material/styles";
import DialogContentText from "@mui/material/DialogContentText";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
// Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import Paper from "@limitless/modern/surfaces/paper/Paper";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Dialog from "@limitless/modern/feedback/dialog/Dialog";
import DataTable from "@limitless/modern/data-display/table/DataTable.js";
import { useExpertRatingContext } from "@limitless/expert";
import { BoxRenderer } from "@limitless/portal";
import { LoadingSpinner } from "@limitless/portal";
import { RATING_COLUMNS } from "./RatingColumn";
import RatingMessage from "./RatingMessage";
import Filters from "./Filters";
import RatingSort from "./RatingSort";

const boxRender = ["secondary-main", "warning-light", "red"];

const RatingTable = () => {
    const [ratingInfoOpen, setRatingInfoOpen] = React.useState(false);
    const [ratingMessageInfoOpen, setRatingMessageInfoOpen] = React.useState(false);
    const [message, setMessage] = React.useState(false);
    const [isGenaiReviewMessage, setIsGenaiReviewMessage] = React.useState(false);

    const [messageIndex, setMessageIndex] = React.useState(0);

    const theme = useTheme();

    const {
        ratingMessages,
        ratingMessageFilters,
        loadExpertRatingMessages,
        group,
        page,
        setPage,
        pageCount,
        filters,
        sort
    } = useExpertRatingContext();

    React.useEffect(() => {
        loadExpertRatingMessages(page, filters, sort);
    }, [page, group, filters, sort]);

    const height = React.useMemo(() => {
        return window.innerHeight > 800 ? 415 : 315;
    }, [window.innerHeight]);

    const handleRatingInfoToggle = () => {
        setRatingInfoOpen(!ratingInfoOpen);
    };

    const actionHandler = tableData => {
        setMessageIndex(ratingMessages.filtered.findIndex(x => x.messageId === tableData.rowData[4]));
        setMessage(ratingMessages.filtered.filter(item => item.messageId === tableData.rowData[4]));
        setIsGenaiReviewMessage(
            ratingMessages.filtered.filter(
                item => item.isGenaiReview === tableData.rowData[7] && item.messageId === tableData.rowData[4]
            )
        );

        setRatingMessageInfoOpen(true);
    };

    const columns = [
        ...RATING_COLUMNS,
        {
            name: "action",
            label: "Message Details",
            options: {
                customBodyRender: (value, tableData) => {
                    return (
                        <Grid
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent="space-between"
                            alignItems={"center"}
                            onClick={() => actionHandler(tableData)}
                            sx={{ cursor: "pointer", color: theme => theme.palette.primary.main }}
                        >
                            <Typography color="primary.main" variant="caption">
                                View{" "}
                            </Typography>
                            <KeyboardArrowRight sx={{ width: "20px" }} />
                        </Grid>
                    );
                },
                setCellProps: () => ({ width: "100px" })
            }
        }
    ];

    const ratingTable = React.useMemo(() => {
        return (
            <DataTable
                columns={columns}
                data={ratingMessages?.filtered}
                option={{
                    sort: false,
                    fixedHeader: true,
                    tableBodyMaxHeight:
                        ratingMessages?.filtered.length > 10
                            ? `calc(100vh - ${height}px)`
                            : `calc(100vh - ${height - 50}px)`
                }}
                loadmore={page < pageCount}
                tableRefreshFunc={() => {
                    return setPage(page + 1);
                }}
            />
        );
    }, [ratingMessages, ratingMessageFilters, page]);

    return (
        <>
            <Paper
                sx={{
                    maxWidth: "unset",
                    minHeight: "100%",
                    height: "calc(100vh - 245px)"
                }}
                elevation={2}
            >
                <Grid container sx={{ padding: 3, rowGap: 1 }}>
                    <Grid item xs={6}>
                        <Grid item container xs={12} id="ratingsHeading">
                            <Typography variant="h6" color="text.primary">
                                Ratings
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item xs={6}>
                        <Grid
                            container
                            justifyContent="flex-end"
                            sx={{ textAlign: "right", rowGap: theme => theme.spacing(1) }}
                            alignItems="flex-end"
                        >
                            <RatingSort />
                            <Filters />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} display="flex" flexDirection="row" justifyContent="space-between">
                        <Grid>
                            <Typography variant="caption" color="text.secondary">
                                All tasks that received a customer or quality rating.
                            </Typography>
                            <Typography
                                style={{
                                    color: theme.palette.primary.main,
                                    display: "inline",
                                    cursor: "pointer",
                                    marginLeft: "5px"
                                }}
                                color="primary.main"
                                variant="caption"
                                id="contactUs"
                                onClick={() => handleRatingInfoToggle(true)}
                            >
                                Learn more
                            </Typography>
                        </Grid>
                        {ratingMessages?.filtered.length !== 0 && (
                            <Grid flexDirection="row" display="flex" alignItems="baseline" sx={{ mr: "4px" }}>
                                {boxRender.map(value => (
                                    <BoxRenderer
                                        key={value}
                                        value=""
                                        color={value}
                                        variant="caption"
                                        containerStyles={{ padding: "4px 0px" }}
                                    />
                                ))}
                                <Typography variant="caption" color="text.primary">
                                    included in rating calcuation
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            height: "calc(100vh - 260px)",
                            overflow: "auto",
                            marginTop: 1,
                            marginBottom: 1,
                            textAlign: "center"
                        }}
                    >
                        {ratingMessages?.filtered?.length > 0
                            ? ratingTable
                            : ratingMessages?.loading && <LoadingSpinner color="primary" />}
                        {!ratingMessages?.loading && ratingMessages?.filtered.length === 0 && (
                            <Grid
                                item
                                xs={12}
                                component={Typography}
                                variant="subtitle2"
                                sx={{ paddingBottom: 1, textAlign: "center", marginTop: 5 }}
                                color="text.secondary"
                            >
                                You currently have no ratings
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
            {ratingMessageInfoOpen && (
                <RatingMessage
                    theme={theme}
                    message={message}
                    messageIndex={messageIndex}
                    messages={ratingMessages.filtered}
                    ratingMessageInfoOpen={ratingMessageInfoOpen}
                    setRatingMessageInfoOpen={setRatingMessageInfoOpen}
                    isGenaiReviewMessage={isGenaiReviewMessage}
                />
            )}
            <Dialog
                open={ratingInfoOpen}
                aria-labelledby="rating-info"
                title="About your rating"
                okText="Close"
                onClose={handleRatingInfoToggle}
                onOkClick={() => handleRatingInfoToggle(false)}
                paperStyle={{ maxWidth: "567px" }}
            >
                <DialogContentText variant="subtitle2">How is my rating calculated?</DialogContentText>
                <DialogContentText variant="body2">
                    {
                        "It’s an average of the most recent customer and quality reviews. A minimum of 5 reviews are required to have a rating and a maximum of 20 reviews (10 of each type) are used in the average."
                    }
                </DialogContentText>
                <DialogContentText variant="subtitle2">What is a customer review? </DialogContentText>
                <DialogContentText variant="body2">
                    {
                        "A customer review measures how satisfied customers are with your help. Customers will give you a score based on the question 'how did the Expert do?"
                    }
                </DialogContentText>
                <DialogContentText variant="subtitle2">What is a quality review?</DialogContentText>
                <DialogContentText variant="body2">
                    {
                        "Quality reviews use a comprehensive and consistent scorecard that encompasses various core values with weighted percentages. These values include factors such as your understanding of the query the consumer asks, your empathy, the resolution you provide and so on. Quality reviews are performed on a subset of messages after closure by designated Limitless GigCoaches."
                    }
                </DialogContentText>
            </Dialog>
        </>
    );
};

export default RatingTable;
